import { useState, useRef, useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import { useNavigate } from 'react-router'
import { theme } from '../../styles/theme'
import {
    useCarlaoNotifications,
    useMarkNotificationRead,
} from '../../hooks/carlao-notifications'
import type { CarlaoNotification } from '../../services/carlao-notifications.service'

// SVG inline pro sininho
function IconBell({ size = 20 }: { size?: number }) {
    return (
        <svg style={{ width: size, height: size, flexShrink: 0 }} viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
    )
}

function IconClose({ size = 14 }: { size?: number }) {
    return (
        <svg style={{ width: size, height: size, flexShrink: 0 }} viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    )
}

// Styled components — design premium consistente com WizardCoach / PatientHeader
const Container = styled.div`
    position: relative;
`

const Trigger = styled.button`
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: transparent;
    border: 1px solid ${theme.colors.borderSubtle};
    color: ${theme.colors.text};
    cursor: pointer;
    transition: background 0.18s ease, border-color 0.18s ease;
    &:hover {
        background: ${theme.colors.background};
        border-color: ${theme.colors.primary};
    }
`

const UnreadBadge = styled.span`
    position: absolute;
    top: -2px;
    right: -2px;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 999px;
    background: linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.tertiary});
    color: ${theme.colors.white};
    font-size: 10px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 6px rgba(243, 149, 63, 0.4);
`

const DropdownAnim = keyframes`
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
`

const Dropdown = styled.div`
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    width: 380px;
    max-height: 480px;
    background: ${theme.colors.white};
    border: 1px solid ${theme.colors.borderSubtle};
    border-radius: 14px;
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.12);
    z-index: 100;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: ${DropdownAnim} 0.16s ease-out;

    @media (max-width: 480px) {
        position: fixed;
        top: auto;
        bottom: 0;
        left: 0;
        right: 0;
        width: auto;
        max-height: 70vh;
        border-radius: 14px 14px 0 0;
    }
`

const DropdownHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-bottom: 1px solid ${theme.colors.borderSubtle};
    flex-shrink: 0;
`

const Title = styled.h3`
    font-size: 14px;
    font-weight: 700;
    color: ${theme.colors.text};
    margin: 0;
    letter-spacing: -0.005em;
`

const CloseBtn = styled.button`
    background: transparent;
    border: none;
    color: ${theme.colors.textGray};
    cursor: pointer;
    padding: 4px;
    border-radius: 6px;
    &:hover { background: ${theme.colors.background}; }
`

const List = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 4px 0;
`

const Item = styled.button<{ $unread: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
    text-align: left;
    background: ${(p) => (p.$unread ? 'rgba(243, 149, 63, 0.04)' : 'transparent')};
    border: none;
    border-bottom: 1px solid ${theme.colors.borderSubtle};
    padding: 12px 16px;
    cursor: pointer;
    transition: background 0.15s ease;
    &:hover { background: ${theme.colors.background}; }
    &:last-child { border-bottom: none; }
`

const ItemTitle = styled.span<{ $unread: boolean }>`
    font-size: 13px;
    font-weight: ${(p) => (p.$unread ? 700 : 500)};
    color: ${theme.colors.text};
`

const ItemBody = styled.span`
    font-size: 12px;
    color: ${theme.colors.textGray};
    line-height: 1.4;
`

const ItemTime = styled.span`
    font-size: 11px;
    color: ${theme.colors.textGray};
    opacity: 0.65;
    margin-top: 4px;
`

const Empty = styled.div`
    padding: 32px 16px;
    text-align: center;
    color: ${theme.colors.textGray};
    font-size: 13px;
`

function formatRelative(iso: string): string {
    const now = Date.now()
    const then = new Date(iso).getTime()
    const diff = Math.max(0, now - then)
    const min = Math.floor(diff / 60000)
    if (min < 1) return 'agora'
    if (min < 60) return `há ${min}min`
    const h = Math.floor(min / 60)
    if (h < 24) return `há ${h}h`
    const d = Math.floor(h / 24)
    return `há ${d}d`
}

function describeNotification(n: CarlaoNotification): { title: string; body: string } {
    if (n.type === 'wizard_refused') {
        const reason = n.payload?.reason ?? '(sem motivo)'
        const patient = n.payload?.patient_name ?? 'paciente'
        return {
            title: `Acréscimo de ${patient} foi recusado`,
            body: `Motivo: ${reason}`,
        }
    }
    if (n.type === 'wizard_approved') {
        const patient = n.payload?.patient_name ?? 'paciente'
        return {
            title: `Acréscimo de ${patient} aprovado`,
            body: 'O laboratório aprovou e o acréscimo foi criado.',
        }
    }
    return { title: 'Notificação', body: '' }
}

export function NotificationBell() {
    const [open, setOpen] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const navigate = useNavigate()

    const { data, isLoading } = useCarlaoNotifications(1, 20)
    const markRead = useMarkNotificationRead()

    const unread = data?.unread ?? 0
    const items = data?.items ?? []

    // Click outside fecha
    useEffect(() => {
        if (!open) return
        function onClick(e: MouseEvent) {
            if (!containerRef.current?.contains(e.target as Node)) {
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', onClick)
        return () => document.removeEventListener('mousedown', onClick)
    }, [open])

    function handleItemClick(n: CarlaoNotification) {
        if (!n.read_at) markRead.mutate(n.id)
        // Navega pra sessão do Carlão (rota nova)
        navigate(`/carlao/novo-acrescimo/${n.session_id}`)
        setOpen(false)
    }

    return (
        <Container ref={containerRef}>
            <Trigger
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-label={`Notificações${unread > 0 ? ` (${unread} não lidas)` : ''}`}
            >
                <IconBell size={20} />
                {unread > 0 && <UnreadBadge>{unread > 99 ? '99+' : unread}</UnreadBadge>}
            </Trigger>
            {open && (
                <Dropdown role="dialog" aria-label="Lista de notificações">
                    <DropdownHeader>
                        <Title>Notificações</Title>
                        <CloseBtn type="button" onClick={() => setOpen(false)} aria-label="Fechar">
                            <IconClose size={14} />
                        </CloseBtn>
                    </DropdownHeader>
                    <List>
                        {isLoading && <Empty>Carregando…</Empty>}
                        {!isLoading && items.length === 0 && <Empty>Sem notificações.</Empty>}
                        {items.map((n) => {
                            const { title, body } = describeNotification(n)
                            return (
                                <Item
                                    key={n.id}
                                    $unread={!n.read_at}
                                    type="button"
                                    onClick={() => handleItemClick(n)}
                                >
                                    <ItemTitle $unread={!n.read_at}>{title}</ItemTitle>
                                    <ItemBody>{body}</ItemBody>
                                    <ItemTime>{formatRelative(n.created_at)}</ItemTime>
                                </Item>
                            )
                        })}
                    </List>
                </Dropdown>
            )}
        </Container>
    )
}
